import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getPaymentStatus, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { clear } = useCart();
  const { user } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [detail, setDetail] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    document.title = "Order Confirmed — Motion & Meaning";
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setDetail(res);
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (res.status === "expired" || res.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch {
        // keep polling
      }
      if (attempts >= 8) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [clear]);

  return (
    <section className="min-h-screen flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="mm-vignette" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        className="relative z-10 text-center max-w-lg"
      >
        {state === "checking" && (
          <>
            <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-6 animate-pulse">CONFIRMING TRANSMISSION</p>
            <h1 className="font-serif text-5xl md:text-6xl text-white">Aligning your order…</h1>
          </>
        )}
        {state === "paid" && (
          <>
            <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-6" data-testid="payment-paid">ORDER CONFIRMED</p>
            <h1 className="font-serif text-6xl md:text-7xl text-white">THANK YOU.</h1>
            {detail?.amount != null && (
              <p className="text-white/60 mt-6">
                {money(detail.amount, detail.currency)} received. A confirmation will follow.
              </p>
            )}
            <p className="text-white/40 text-sm mt-3">Every motion carries meaning.</p>

            {user ? (
              <div className="mt-12 flex flex-col items-center gap-4">
                <button
                  onClick={() => navigate("/account")}
                  className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-9 py-4 hover:bg-white transition-colors"
                  data-testid="success-view-account"
                >
                  VIEW IN YOUR ACCOUNT
                </button>
                <button
                  onClick={() => navigate("/store")}
                  className="font-mono text-[11px] tracking-[0.2em] text-white/50 hover:text-gold transition-colors"
                  data-testid="success-back-store"
                >
                  RETURN TO STORE
                </button>
              </div>
            ) : (
              <div className="mt-12 border border-white/10 p-8 max-w-sm mx-auto">
                <p className="font-mono text-[10px] tracking-[0.25em] text-gold mb-3">OPTIONAL</p>
                <p className="text-white/70 text-sm leading-relaxed mb-6">
                  Create an account to keep this order, your downloads and future access in one place.
                </p>
                <button
                  onClick={() => navigate("/register")}
                  className="w-full bg-gold text-black font-mono text-[11px] tracking-[0.25em] py-4 hover:bg-white transition-colors"
                  data-testid="success-create-account"
                >
                  CREATE OR CONNECT ACCOUNT
                </button>
                <button
                  onClick={() => navigate("/store")}
                  className="mt-4 font-mono text-[11px] tracking-[0.2em] text-white/40 hover:text-gold transition-colors"
                  data-testid="success-continue-guest"
                >
                  CONTINUE AS GUEST →
                </button>
              </div>
            )}
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <p className="font-mono text-[11px] tracking-[0.4em] text-white/50 mb-6">STATUS PENDING</p>
            <h1 className="font-serif text-4xl md:text-5xl text-white">
              {state === "error" ? "We couldn't confirm this order." : "Still processing…"}
            </h1>
            <p className="text-white/50 mt-6">
              {state === "error"
                ? "No charge may have completed. Please try again."
                : "Your payment is taking longer than usual. Check your email for confirmation."}
            </p>
            <button
              onClick={() => navigate("/store")}
              className="mt-12 border border-white/30 font-mono text-[11px] tracking-[0.25em] px-9 py-4 hover:border-gold hover:text-gold transition-colors"
            >
              RETURN TO STORE
            </button>
          </>
        )}
      </motion.div>
    </section>
  );
}
